// journalApi.js - API-funktioner för journalmodulen

import apiClient from '../../apiClient';

const BASE_URL = '/api/journal';

/**
 * True om journalinnehållet saknar all text (alla sektioner tomma).
 * Speglar backendens skyddsvakt i sign_journal_entry (Spår F) så att UI:t kan
 * neka signering av tom journal innan servern gör det.
 * @param {object} content - { sections: [{ title, content }] }
 */
export const isJournalContentEmpty = (content) => {
  if (!content || typeof content !== 'object') return true;
  const sections = content.sections || [];
  return !sections.some(
    (s) => s && typeof s.content === 'string' && s.content.trim()
  );
};

// ============== JOURNAL ENTRIES API ==============

/**
 * Hämta journalanteckningar för en patient
 * @param {number} patientId - Patient-ID
 * @param {object} options - { status, limit, offset }
 */
export const getJournalEntries = async (patientId, options = {}) => {
  const params = { patient_id: patientId };
  if (options.status) params.status = options.status;
  if (options.limit) params.limit = options.limit;
  if (options.offset) params.offset = options.offset;
  const response = await apiClient.get(`${BASE_URL}/entries`, { params });
  return response.data;
};

/**
 * Hämta en specifik journalanteckning med innehåll
 * @param {number} entryId - Journal-ID
 */
export const getJournalEntry = async (entryId) => {
  const response = await apiClient.get(`${BASE_URL}/entries/${entryId}`);
  return response.data;
};

/**
 * Skapa ny journalanteckning
 * @param {object} data - { patient_id, template_ref, appointment_id, notes_session_ref, content }
 */
export const createJournalEntry = async (data) => {
  const response = await apiClient.post(`${BASE_URL}/entries`, data);
  return response.data;
};

/**
 * Uppdatera journalanteckning (endast draft)
 * @param {number} entryId - Journal-ID
 * @param {object} data - { content, template_id }
 */
export const updateJournalEntry = async (entryId, data) => {
  const response = await apiClient.put(`${BASE_URL}/entries/${entryId}`, data);
  return response.data;
};

/**
 * Ta bort journalanteckning (endast draft)
 * @param {number} entryId - Journal-ID
 */
export const deleteJournalEntry = async (entryId) => {
  const response = await apiClient.delete(`${BASE_URL}/entries/${entryId}`);
  return response.data;
};

/**
 * Signera journalanteckning (låser för redigering).
 * Skickas `content` med sparas det och signeras atomärt i samma transaktion
 * på servern, så att text aldrig tappas mellan spara och signering (Spår F).
 * @param {number} entryId - Journal-ID
 * @param {object} [content] - Journalinnehåll att spara innan signering
 * @param {number} [expectedVersion] - versionen innehållet utgår från; avviker
 *   den från serverns svarar backend 409 (Spår J, ingen tyst överskrivning)
 */
export const signJournalEntry = async (entryId, content, expectedVersion) => {
  let body;
  if (content !== undefined) {
    body = { content };
    if (expectedVersion != null) body.expected_version = expectedVersion;
  }
  const response = await apiClient.post(`${BASE_URL}/entries/${entryId}/sign`, body);
  return response.data;
};

// ============== AMENDMENTS (SIGNERAT TILLÄGG) ==============

/**
 * Hämta tillägg till en signerad journal.
 * @param {number} entryId - Journal-ID
 * @returns {Promise<{amendments: Array, can_add_amendment: boolean}>}
 */
export const getAmendments = async (entryId) => {
  const response = await apiClient.get(`${BASE_URL}/entries/${entryId}/amendments`);
  return response.data;
};

/**
 * Skapa ett tillägg till en signerad journal (append-only, auto-signeras).
 * Endast ursprunglig författare tillåts av backend.
 * @param {number} entryId - Journal-ID
 * @param {object} amendment - { amendment_text, amendment_reason }
 */
export const createAmendment = async (entryId, amendment) => {
  const response = await apiClient.post(`${BASE_URL}/entries/${entryId}/amendments`, amendment);
  return response.data;
};

// ============== CLINIC SETTINGS API ==============

/**
 * Hämta klinikinställningar för journalmodulen
 */
export const getClinicSettings = async () => {
  const response = await apiClient.get(`${BASE_URL}/clinic/settings`);
  return response.data;
};

// ============== TEMPLATES API ==============

/**
 * Hämta journalmallar för kliniken
 */
export const getJournalTemplates = async () => {
  const response = await apiClient.get(`${BASE_URL}/templates`);
  return response.data;
};
